import type { FC } from 'react';
import { useState } from 'react';
import { Paper } from '@mui/material';
import type { SxProps, SvgIconProps } from '@mui/material';
import {
  FuelEfficiency32,
  WetGrip32,
  ExternalRollingNoise32,
  EnergyEfficiency,
  EnergyEfficiencyOld,
} from '@packages/themes/icons';
import { Image } from '../Image';
import { Typography } from '../Typography';
import { Dialog } from '../Dialog';
import { Box } from '../Box';

export type EfficiencyFlagProps = {
  /** flag type */
  type:
    | 'fuelEfficiency'
    | 'wetGrip'
    | 'externalRollingNoise'
    | 'energyEfficiency'
    | 'energyEfficiencyOld';
  /** text displayed in flag */
  text: string;
  /** bgcolor of energy efficiency flags */
  labelColor?:
    | 'darkgreen'
    | 'mediumgreen'
    | 'lightgreen'
    | 'yellow'
    | 'lightorange'
    | 'darkorange'
    | 'red';
  /** overlay when flag is clicked */
  overlay?: {
    title: string;
    imgUrl: string;
  };
  /**
   * large flag variant
   * @default false
   */
  large?: boolean;
  /** MUI Sx Prop */
  sx?: SxProps;

  /** Optional label for efficiency-flag  */
  label?: string;
  /**
   * This callback function is called after the EfficiencyFlag has been clicked.
   */
  onClickHandler?: () => void;
  /**
   * This callback function is called after the dialog close button has been clicked.
   */
  onCloseHandler?: () => void;
};

const labelColorsMap = {
  darkgreen: '#008C3E',
  mediumgreen: '#50A733',
  lightgreen: '#CDD100',
  yellow: '#FFEA00',
  lightorange: '#F8BA00',
  darkorange: '#EC6707',
  red: '#DB0E15',
};

const svgIconsMap = {
  fuelEfficiency: FuelEfficiency32,
  wetGrip: WetGrip32,
  externalRollingNoise: ExternalRollingNoise32,
  energyEfficiency: EnergyEfficiency,
  energyEfficiencyOld: EnergyEfficiencyOld,
};

const Icon = ({
  type,
  ...props
}: {
  type: EfficiencyFlagProps['type'];
} & SvgIconProps) => {
  const ActualIcon = svgIconsMap[type];

  return <ActualIcon {...props} />;
};

/**
 * EfficiencyFlag component with overlay to display energy label
 * */
export const EfficiencyFlag: FC<EfficiencyFlagProps> = ({
  type,
  text,
  labelColor,
  overlay,
  large = false,
  sx,
  label,
  onClickHandler,
  onCloseHandler,
}) => {
  const [showOverlay, setShowOverlay] = useState(false);

  const openOverlay = () => {
    onClickHandler?.();
    setShowOverlay(true);
  };
  const closeOverlay = () => {
    onCloseHandler?.();
    setShowOverlay(false);
  };

  const overlayContent = (
    <Typography
      component="div"
      sx={{
        position: 'relative',
        display: 'flex',
        flexFlow: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 600,
        '> img': {
          objectFit: 'contain',
        },
      }}
    >
      {overlay?.imgUrl && <Image src={overlay.imgUrl} alt={overlay.title} fill unoptimized />}
    </Typography>
  );

  return (
    <>
      <Box
        sx={{ display: 'flex', alignItems: 'center' }}
        component={overlay ? 'a' : 'div'}
        onClick={overlay ? openOverlay : undefined}
      >
        <Paper
          variant="outlined"
          sx={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            borderColor: 'text.dark',
            bgcolor: 'transparent',
            textDecoration: 'none',
            cursor: 'pointer',
            ...(type.includes('energyEfficiency')
              ? {
                  border: 'none',
                }
              : {
                  px: large ? 1 : 0.5,
                  py: 0.25,
                }),
            ...sx,
          }}
        >
          <Icon
            type={type}
            sx={{
              fill: 'inherit',
              color: labelColor ? labelColorsMap[labelColor] : undefined,
              fontSize: large ? 32 : 16,
              ...(type.includes('energyEfficiency') && {
                fontSize: large ? 55 : 45,
                width: large ? 61 : 35,
                height: large ? 35 : 20,
              }),
            }}
          />

          <Typography
            component="p"
            variant={large ? 'h4' : 'body1'}
            sx={{
              ml: large ? 1 : 0.4,
              fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
              '> sup': {
                fontSize: large ? 12 : 8,
              },
              ...(type.includes('energyEfficiency') && {
                position: 'absolute',
                color: 'text.light',
                fontWeight: 700,
              }),
              ...(type === 'energyEfficiency' && {
                ml: large ? 2.5 : 1.5,
              }),
              ...(type === 'energyEfficiencyOld' &&
                text.includes('+') && {
                  top: 0,
                  lineHeight: large ? '28px' : '15px',
                }),
            }}
          >
            {text.includes('+') ? (
              <>
                {text.substring(0, 1)}
                <sup>{text.substring(1)}</sup>
              </>
            ) : (
              text
            )}
          </Typography>
        </Paper>

        {label && (
          <Typography component="span" variant="body2" sx={{ ml: 1, cursor: 'pointer' }}>
            {label}
          </Typography>
        )}
      </Box>
      {overlay && (
        <Dialog
          open={showOverlay}
          handleClose={closeOverlay}
          title={overlay.title}
          dialogContent={overlayContent}
        />
      )}
    </>
  );
};
